import * as React from 'react'
import { FC } from 'react'
import classNames from 'classnames'
import { CSSTransition } from 'react-transition-group'
import { useLocation } from 'react-router-dom'

import { getEnabledLinks, linkActive, Tier1Link, Link } from '../../../../../helpers/navStructure'
import Tier1MenuItem from '../Tier1MenuItem'
import { media } from '../../../../../style/media'
import Tier2MenuItem from '../Tier2MenuItem'
import { ZIndex } from '../../../../../style/variables'

interface DesktopMenuProps {
  isOpen: boolean
  onClose: () => void
  links: Tier1Link[]
  selectedLink: Tier1Link | null
  onSelectedLink: (link: Tier1Link | null) => void
  highlightedTier2Link: Link | null
  onHighlightedTier2Link: (link: Link | null) => void
  highlightedTier3Link: Link | null
  onHighlightedTier3Link: (link: Link | null) => void
}

const animationDuration = 250

const DesktopMenu: FC<DesktopMenuProps> = ({
  isOpen,
  onClose,
  links,
  selectedLink,
  onSelectedLink,
  highlightedTier2Link,
  onHighlightedTier2Link,
  highlightedTier3Link,
  onHighlightedTier3Link,
}) => {
  const washoutClasses = classNames('washout', { 'open-washout': isOpen })
  const location = useLocation()

  const activePath = links.filter((l) => linkActive(l.href, l.children, location.pathname))
  const backgroundImage = selectedLink ? selectedLink.backgroundImageUrl : activePath.length > 0 ? activePath[0].backgroundImageUrl : ''
  const backgroundImageAlt = selectedLink ? selectedLink.backgroudImageAlt : activePath.length > 0 ? activePath[0].backgroudImageAlt : ''

  return (
    <>
      <style jsx>{`
        .washout {
          background-color: rgba(0, 0, 0, 0.8);
          width: 100%;
          position: absolute;
          height: 100vh;
          display: none;
          z-index: ${ZIndex.Menu};
        }
        .menu {
          position: absolute;
          background: #ffffff;
          width: 100%;
          z-index: ${ZIndex.Menu};
          display: none;
          min-height: 640px;
          height: 80vh;
          width: 100vw;
          overflow-x: hidden;
        }
        .menu-items {
          padding-left: 64px;
          padding-top: 52px;
          flex-direction: column;
          display: flex;
          position: relative;
        }
        .tier2-menu-items {
          padding-left: 64px;
          padding-top: 52px;
          flex-direction: column;
          display: flex;
          z-index: 1;
          flex: 0 0 336px;
        }
        .image {
          display: none;
          height: 100%;
          width: 720px;
          background-image: url('/img/topnavbar/menu/open-menu-scarlet-lady.jpg');
          position: relative;
          float: right;
          background-size: cover;
          overflow: hidden;
        }
        .image-wrapper {
          flex-grow: 1;
        }
        @media ${media.desktopAndHigher} {
          .image {
            display: block;
          }
        }
        .open-washout {
          display: block;
        }
        .triangle {
          position: absolute;
          top: 0;
          left: 0;
          width: 0;
          height: 0;
          border-top: 100vh solid white;
          border-right: 401px solid transparent;
        }

        .menu-enter,
        .menu-exit,
        .menu-enter-done {
          display: flex;
          transition: all ${animationDuration}ms;
          transition-timing-function: ease-in-out;
        }
        .menu-enter {
          opacity: 0;
        }

        .menu-enter-active {
          opacity: 1;
          transition: all ${animationDuration}ms;
          transition-timing-function: ease-in-out;
        }
        .menu-exit-active {
          opacity: 0;
          transition: all ${animationDuration}ms;
          transition-timing-function: ease-in-out;
        }

        .menu-exit {
          display: flex;
          opacity: 0;
        }
        .menu-exit-done {
          display: none;
        }
      `}</style>
      <div className={washoutClasses} onClick={onClose}></div>
      <CSSTransition in={isOpen} timeout={animationDuration} classNames="menu">
        <nav className="menu" aria-label="Virgin header navigation">
          <div className="menu-items">
            {getEnabledLinks().map((item) => (
              <Tier1MenuItem
                key={item.href}
                href={item.href}
                label={item.label}
                onSelect={(): void => {
                  onSelectedLink(item)
                  onHighlightedTier2Link(null)
                  onHighlightedTier3Link(null)
                }}
                onNavigate={onClose}
                isLink={item.children.length === 0}
                selected={item.href === selectedLink?.href}
              />
            ))}
          </div>
          <div className="tier2-menu-items">
            {selectedLink && selectedLink?.children.length > 0 && (
              <Tier2MenuItem
                key={selectedLink.href}
                href={selectedLink.href}
                label={selectedLink.altLabel || selectedLink.label}
                childLinks={[]}
                onNavigate={onClose}
                highlighted={selectedLink.href === highlightedTier2Link?.href}
                onHighlighted={(): void => onHighlightedTier2Link(selectedLink)}
                highlightedTier3Link={highlightedTier3Link}
                onHighlightedTier3Link={onHighlightedTier3Link}
              />
            )}
            {selectedLink?.children.map((link) => (
              <Tier2MenuItem
                key={link.href}
                href={link.href}
                label={link.label}
                childLinks={link.children}
                onNavigate={onClose}
                highlighted={link.href === highlightedTier2Link?.href}
                onHighlighted={(): void => onHighlightedTier2Link(link)}
                highlightedTier3Link={highlightedTier3Link}
                onHighlightedTier3Link={onHighlightedTier3Link}
              />
            ))}
          </div>
          <div className="image-wrapper" aria-hidden={true} role="presentation">
            <div
              className="image"
              role="img"
              aria-label={backgroundImageAlt}
              style={{
                backgroundImage: `url('${backgroundImage}')`,
              }}>
              <div className="triangle"></div>
            </div>
          </div>
        </nav>
      </CSSTransition>
    </>
  )
}

export default DesktopMenu
